const EMAIL_LINK_PATTERN = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/g;
const GIFT_CART_EVENTS = new Set(['gift:remove']);
const FREE_GIFT_PROPERTY_KEY = '_free_gift';
const FREE_GIFT_PROPERTY_VALUE = 'true';

function isFreeGiftLineItem(item) {
  const propertyValue = item?.properties?.[FREE_GIFT_PROPERTY_KEY];
  return propertyValue === FREE_GIFT_PROPERTY_VALUE || propertyValue === true;
}

function getCartTotalExcludingGift(cart) {
  let total = cart.items
    .filter((item) => !isFreeGiftLineItem(item))
    .reduce((sum, item) => sum + item.final_line_price, 0);

  if (cart.cart_level_discount_applications?.length) {
    total -= cart.cart_level_discount_applications.reduce(
      (sum, discount) => sum + discount.total_allocated_amount,
      0
    );
  }

  return total;
}

function getGiftLineItem(cart) {
  return cart.items.find(isFreeGiftLineItem);
}

async function fetchCartState() {
  const response = await fetch(`${Shopify.routes.root}cart.js`);
  return response.json();
}

async function updateGiftInCart({ giftLineItem, shouldRemoveGift }) {
  if (!shouldRemoveGift || !giftLineItem) {
    return;
  }

  const sectionsToBundle = new Set();
  document.documentElement.dispatchEvent(
    new CustomEvent('cart:prepare-bundled-sections', {
      bubbles: true,
      detail: { sections: sectionsToBundle }
    })
  );

  document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

  const response = await fetch(`${Shopify.routes.root}cart/change.js`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      id: giftLineItem.key,
      quantity: 0,
      sections: [...sectionsToBundle].join(',')
    })
  });

  document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));

  if (!response.ok) {
    return;
  }

  if (window.themeVariables?.settings?.pageType === 'cart') {
    window.location.reload();
    return;
  }

  const responseJson = await response.json();
  const cartContent = await fetchCartState();
  cartContent.sections = responseJson.sections || {};

  document.documentElement.dispatchEvent(
    new CustomEvent('cart:change', {
      bubbles: true,
      detail: {
        baseEvent: 'gift:remove',
        cart: cartContent
      }
    })
  );
}

class CartGiftManager extends HTMLElement {
  #processing = false;
  #abortController = null;

  connectedCallback() {
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    document.addEventListener('cart:change', (event) => this.#handleCartChange(event), { signal });
    document.addEventListener('cart:refresh', () => this.#handleCartChange(), { signal });
    this.#handleCartChange();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = null;
  }

  async #handleCartChange(event) {
    if (this.#processing) {
      return;
    }

    if (event?.detail?.baseEvent && GIFT_CART_EVENTS.has(event.detail.baseEvent)) {
      return;
    }

    const threshold = Number(this.dataset.threshold);

    if (!threshold) {
      return;
    }

    this.#processing = true;

    try {
      const cart = event?.detail?.cart ?? (await fetchCartState());
      const giftLineItem = getGiftLineItem(cart);
      const cartTotal = getCartTotalExcludingGift(cart);
      const shouldRemoveGift = Boolean(giftLineItem && cartTotal < threshold);

      await updateGiftInCart({
        giftLineItem,
        shouldRemoveGift
      });
    } finally {
      this.#processing = false;
    }
  }
}

if (!window.customElements.get('cart-gift-manager')) {
  window.customElements.define('cart-gift-manager', CartGiftManager);
}

class CartGiftPromoForm extends HTMLElement {
  #abortController = null;

  connectedCallback() {
    this.#abortController = new AbortController();
    this.querySelector('[data-cart-gift-add]')?.addEventListener(
      'click',
      (event) => this.#onAdd(event),
      { signal: this.#abortController.signal }
    );
    this.querySelector('[data-cart-gift-remove]')?.addEventListener(
      'click',
      (event) => this.#onRemove(event),
      { signal: this.#abortController.signal }
    );
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = null;
  }

  async #onAdd(event) {
    const button = event.currentTarget;

    if (button.disabled || button.getAttribute('aria-busy') === 'true') {
      return;
    }

    const variantId = Number(this.dataset.variantId);

    if (!variantId) {
      return;
    }

    button.setAttribute('aria-busy', 'true');

    const sectionsToBundle = new Set();
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', {
        bubbles: true,
        detail: { sections: sectionsToBundle }
      })
    );

    document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

    const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({
        items: [
          {
            id: variantId,
            quantity: 1,
            properties: {
              [FREE_GIFT_PROPERTY_KEY]: FREE_GIFT_PROPERTY_VALUE
            }
          }
        ],
        sections: [...sectionsToBundle].join(','),
        sections_url: window.location.pathname
      })
    });

    button.removeAttribute('aria-busy');
    document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));

    const responseJson = await response.json();

    if (response.ok) {
      if (window.themeVariables?.settings?.cartType === 'page' || window.themeVariables?.settings?.pageType === 'cart') {
        window.location.href = `${Shopify.routes.root}cart`;
        return;
      }

      const cartContent = await fetchCartState();
      cartContent.sections = responseJson.sections || {};
      const items = Object.prototype.hasOwnProperty.call(responseJson, 'items') ? responseJson.items : [responseJson];

      document.documentElement.dispatchEvent(
        new CustomEvent('cart:change', {
          bubbles: true,
          detail: {
            baseEvent: 'variant:add',
            cart: cartContent
          }
        })
      );

      if (window.themeVariables?.settings?.cartType === 'message') {
        document.dispatchEvent(
          new CustomEvent('toast:show', {
            detail: {
              message: window.themeVariables.strings.addedToCart.replace('{{product_title}}', items?.[0]?.title || '')
            }
          })
        );
      }

      return;
    }

    document.dispatchEvent(
      new CustomEvent('toast:show', {
        detail: {
          message: responseJson.description || responseJson.message,
          tone: 'error'
        }
      })
    );
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
  }

  async #onRemove(event) {
    const button = event.currentTarget;

    if (button.disabled || button.getAttribute('aria-busy') === 'true') {
      return;
    }

    const lineKey = this.dataset.lineKey;

    if (!lineKey) {
      return;
    }

    button.setAttribute('aria-busy', 'true');

    const cart = await fetchCartState();
    const giftLineItem = cart.items.find((item) => item.key === lineKey);

    await updateGiftInCart({
      giftLineItem,
      shouldRemoveGift: Boolean(giftLineItem)
    });

    button.removeAttribute('aria-busy');
  }
}

if (!window.customElements.get('cart-gift-promo-form')) {
  window.customElements.define('cart-gift-promo-form', CartGiftPromoForm);
}

const CART_REMOVE_UNDO_DURATION = 5000;
const CART_REMOVE_UNDO_FADE_DURATION = 300;

function escapeHtml(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function getBundledSectionIds() {
  const sectionsToBundle = new Set();
  document.documentElement.dispatchEvent(
    new CustomEvent('cart:prepare-bundled-sections', {
      bubbles: true,
      detail: { sections: sectionsToBundle }
    })
  );
  return [...sectionsToBundle];
}

async function changeCartLineQuantity(lineKey, quantity) {
  document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

  const response = await fetch(`${Shopify.routes.root}cart/change.js`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      id: lineKey,
      quantity,
      sections: getBundledSectionIds().join(',')
    })
  });

  document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
  return response;
}

async function addCartLineItem(item) {
  const payload = {
    id: item.variant_id,
    quantity: item.quantity,
    properties: item.properties || {}
  };

  if (item.selling_plan_allocation?.selling_plan?.id) {
    payload.selling_plan = item.selling_plan_allocation.selling_plan.id;
  }

  document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

  const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'X-Requested-With': 'XMLHttpRequest'
    },
    body: JSON.stringify({
      items: [payload],
      sections: getBundledSectionIds().join(','),
      sections_url: window.location.pathname
    })
  });

  document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
  return response;
}

async function dispatchCartChange(responseJson, baseEvent) {
  const cartContent = await fetchCartState();
  cartContent.sections = responseJson.sections || {};

  document.documentElement.dispatchEvent(
    new CustomEvent('cart:change', {
      bubbles: true,
      detail: {
        baseEvent,
        cart: cartContent
      }
    })
  );

  return cartContent;
}

function getCartRemoveUndoSettings(source) {
  return {
    message: source?.dataset.removeUndoMessage || 'Dieser Artikel wurde entfernt.',
    undoText: source?.dataset.removeUndoButtonText || 'Rückgängig'
  };
}

class CartRemoveUndo {
  #drawerUndo = null;
  #drawerUndoTimer = null;
  #mainCartUndo = null;
  #mainCartUndoTimer = null;
  #mainCartUndoFadeTimer = null;
  #processing = false;

  constructor() {
    document.addEventListener('click', (event) => this.#onRemoveClick(event), true);
  }

  async #onRemoveClick(event) {
    if (this.#processing) {
      return;
    }

    const link = event.target.closest('a[href*="cart/change"]');

    if (!link || link.closest('.cart-gift-promo-form')) {
      return;
    }

    const url = new URL(link.href, window.location.origin);
    const quantity = Number.parseInt(url.searchParams.get('quantity'), 10);

    if (quantity !== 0) {
      return;
    }

    const cartDrawer = link.closest('cart-drawer');
    const isMainCart = window.themeVariables?.settings?.pageType === 'cart';

    if (!cartDrawer && !isMainCart) {
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();

    const lineKey = url.searchParams.get('id');

    if (!lineKey) {
      return;
    }

    const cart = await fetchCartState();
    const removedItem = cart.items.find((item) => item.key === lineKey);

    if (!removedItem || isFreeGiftLineItem(removedItem)) {
      return;
    }

    if (cartDrawer) {
      await this.#handleDrawerRemove(cartDrawer, link, lineKey, removedItem);
      return;
    }

    await this.#handleMainCartRemove(link, lineKey, removedItem);
  }

  async #handleDrawerRemove(cartDrawer, link, lineKey, removedItem) {
    const lineItem = link.closest('line-item.line-item');
    const lineItems = [...cartDrawer.querySelectorAll('line-item.line-item')];
    const lineIndex = lineItem ? lineItems.indexOf(lineItem) : Math.max(lineItems.length - 1, 0);

    this.#processing = true;

    try {
      const response = await changeCartLineQuantity(lineKey, 0);

      if (!response.ok) {
        return;
      }

      const responseJson = await response.json();
      await dispatchCartChange(responseJson, 'line-item:remove-with-undo');

      window.requestAnimationFrame(() => {
        this.#showDrawerUndo(removedItem, lineIndex);
      });
    } finally {
      this.#processing = false;
    }
  }

  async #handleMainCartRemove(link, lineKey, removedItem) {
    this.#processing = true;

    try {
      const response = await changeCartLineQuantity(lineKey, 0);

      if (!response.ok) {
        window.location.href = link.href;
        return;
      }

      const responseJson = await response.json();
      link.closest('tr')?.remove();

      await dispatchCartChange(responseJson, 'line-item:remove-with-undo');
      await this.#updateMainCartSidebar(responseJson);

      if (responseJson.item_count === 0) {
        this.#showMainCartUndo(removedItem, () => {
          window.location.reload();
        });
      } else {
        this.#showMainCartUndo(removedItem);
      }
    } finally {
      this.#processing = false;
    }
  }

  #clearDrawerUndo() {
    window.clearTimeout(this.#drawerUndoTimer);
    this.#drawerUndoTimer = null;
    this.#drawerUndo?.element?.remove();
    this.#drawerUndo = null;
  }

  #clearMainCartUndo() {
    window.clearTimeout(this.#mainCartUndoTimer);
    window.clearTimeout(this.#mainCartUndoFadeTimer);
    this.#mainCartUndoTimer = null;
    this.#mainCartUndoFadeTimer = null;
    this.#mainCartUndo?.element?.remove();
    this.#mainCartUndo = null;
  }

  async #showDrawerUndo(removedItem, lineIndex) {
    const cartDrawer = document.getElementById('cart-drawer');

    if (!cartDrawer || !removedItem) {
      return;
    }

    this.#clearDrawerUndo();

    const { message, undoText } = getCartRemoveUndoSettings(cartDrawer);
    const undoElement = document.createElement('div');
    undoElement.className = 'cart-remove-undo cart-remove-undo--inline';
    undoElement.innerHTML = `
      <div class="cart-remove-undo__content">
        <span class="cart-remove-undo__icon" aria-hidden="true">
          <svg width="10" viewBox="0 0 14 12" focusable="false">
            <path fill="none" stroke="currentColor" d="m1 6 4 4 8-8"></path>
          </svg>
        </span>
        <span class="cart-remove-undo__message subheading">${escapeHtml(message)}</span>
      </div>
      <button type="button" class="cart-remove-undo__button link subheading">${escapeHtml(undoText)}</button>
    `;

    const lineItems = cartDrawer.querySelectorAll('line-item.line-item');

    if (lineItems.length > 0 && lineIndex >= 0 && lineIndex < lineItems.length) {
      lineItems[lineIndex].before(undoElement);
    } else if (lineItems.length > 0) {
      lineItems[lineItems.length - 1].after(undoElement);
    } else {
      const emptyState = cartDrawer.querySelector('.cart-drawer-empty');
      const itemsContainer = cartDrawer.querySelector('.v-stack.gap-4');

      if (emptyState) {
        emptyState.insertBefore(undoElement, emptyState.querySelector('.empty-state') || null);
      } else if (itemsContainer) {
        itemsContainer.prepend(undoElement);
      } else {
        cartDrawer.querySelector('[slot="tabpanel"]')?.prepend(undoElement);
      }
    }

    undoElement.querySelector('.cart-remove-undo__button')?.addEventListener('click', () => {
      this.#undoDrawerRemoval(removedItem);
    });

    this.#drawerUndo = { element: undoElement, item: removedItem };
    this.#drawerUndoTimer = window.setTimeout(() => this.#clearDrawerUndo(), CART_REMOVE_UNDO_DURATION);
  }

  #showMainCartUndo(removedItem, onExpire) {
    this.#clearMainCartUndo();

    const section = document.querySelector('.shopify-section--main-cart');
    const { message, undoText } = getCartRemoveUndoSettings(section);
    const schemeClasses = [...(section?.querySelector('.color-scheme')?.classList || [])]
      .filter((className) => className.startsWith('color-scheme'))
      .join(' ');
    const toast = document.createElement('div');
    toast.className = `cart-remove-undo cart-remove-undo--toast ${schemeClasses || 'color-scheme color-scheme--scheme-1'}`;
    toast.setAttribute('role', 'status');
    toast.innerHTML = `
      <div class="cart-remove-undo__content">
        <span class="cart-remove-undo__icon" aria-hidden="true">
          <svg width="10" viewBox="0 0 14 12" focusable="false">
            <path fill="none" stroke="currentColor" d="m1 6 4 4 8-8"></path>
          </svg>
        </span>
        <span class="cart-remove-undo__message subheading">${escapeHtml(message)}</span>
      </div>
      <button type="button" class="cart-remove-undo__button link subheading">${escapeHtml(undoText)}</button>
    `;

    toast.querySelector('.cart-remove-undo__button')?.addEventListener('click', () => {
      this.#undoMainCartRemoval(removedItem);
    });

    document.body.appendChild(toast);

    this.#mainCartUndo = { element: toast, item: removedItem, onExpire };
    this.#mainCartUndoTimer = window.setTimeout(() => {
      toast.classList.add('is-fading');
      this.#mainCartUndoFadeTimer = window.setTimeout(() => {
        const expireCallback = onExpire;
        this.#clearMainCartUndo();
        expireCallback?.();
      }, CART_REMOVE_UNDO_FADE_DURATION);
    }, CART_REMOVE_UNDO_DURATION);
  }

  async #undoDrawerRemoval(removedItem) {
    if (this.#processing) {
      return;
    }

    this.#processing = true;
    this.#clearDrawerUndo();

    try {
      const response = await addCartLineItem(removedItem);

      if (!response.ok) {
        return;
      }

      const responseJson = await response.json();
      await dispatchCartChange(responseJson, 'line-item:undo-remove');
    } finally {
      this.#processing = false;
    }
  }

  async #undoMainCartRemoval(removedItem) {
    if (this.#processing) {
      return;
    }

    this.#processing = true;
    this.#clearMainCartUndo();

    try {
      const response = await addCartLineItem(removedItem);

      if (!response.ok) {
        return;
      }

      const responseJson = await response.json();
      await dispatchCartChange(responseJson, 'line-item:undo-remove');
      await this.#refreshMainCartSection();
    } finally {
      this.#processing = false;
    }
  }

  async #updateMainCartSidebar(cart) {
    const section = document.querySelector('.shopify-section--main-cart');

    if (!section) {
      return;
    }

    const sectionId = section.id.replace('shopify-section-', '');
    const response = await fetch(`${window.location.pathname}?section_id=${sectionId}`);
    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const nextSidebar = doc.querySelector('.cart__right');
    const currentSidebar = section.querySelector('.cart__right');

    if (nextSidebar && currentSidebar) {
      currentSidebar.innerHTML = nextSidebar.innerHTML;
    }

    const nextGiftBar = doc.querySelector('.cart-header .cart-gift-bar');
    const currentGiftBar = section.querySelector('.cart-header .cart-gift-bar');

    if (nextGiftBar && currentGiftBar) {
      currentGiftBar.replaceWith(nextGiftBar);
    } else if (nextGiftBar && !currentGiftBar) {
      section.querySelector('.cart-header')?.append(nextGiftBar);
    } else {
      currentGiftBar?.remove();
    }

    const nextGiftPromo = doc.querySelector('.cart__left .cart-gift-promo, .cart__left .cart-gift-promo-form');
    const currentGiftPromo = section.querySelector('.cart__left .cart-gift-promo, .cart__left .cart-gift-promo-form');

    if (nextGiftPromo && currentGiftPromo) {
      currentGiftPromo.replaceWith(nextGiftPromo);
    } else if (nextGiftPromo && !currentGiftPromo) {
      section.querySelector('.cart__left')?.append(nextGiftPromo);
    } else {
      currentGiftPromo?.remove();
    }

    if (cart.item_count === 0) {
      return;
    }
  }

  async #refreshMainCartSection() {
    const section = document.querySelector('.shopify-section--main-cart');

    if (!section) {
      window.location.reload();
      return;
    }

    const sectionId = section.id.replace('shopify-section-', '');
    const response = await fetch(`${window.location.pathname}?section_id=${sectionId}`);
    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const nextSection = doc.getElementById(section.id);

    if (!nextSection) {
      window.location.reload();
      return;
    }

    section.replaceWith(nextSection);
  }
}

new CartRemoveUndo();

function applyWishlistPageScheme() {
  const wishlistMain = document.querySelector('.wishlist-page-main');
  if (!wishlistMain) return false;

  wishlistMain.classList.add('color-scheme', 'color-scheme--scheme-1');
  return true;
}

function applyWishlistDefaultGrid() {
  if (typeof gridFxn !== 'function') return false;

  const grid4Buttons = document.querySelectorAll('.wishlist-page-main .grid4');
  if (!grid4Buttons.length) return false;

  const isActive = [...grid4Buttons].some((button) => button.classList.contains('wf-active-grid-focus'));
  if (!isActive) gridFxn('4');

  return true;
}

function buildWishlistQuickAddIcon(label) {
  const fragment = document.createDocumentFragment();

  const srOnly = document.createElement('span');
  srOnly.className = 'sr-only';
  srOnly.textContent = label;
  fragment.appendChild(srOnly);

  const icon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  icon.setAttribute('aria-hidden', 'true');
  icon.setAttribute('focusable', 'false');
  icon.setAttribute('width', '20');
  icon.setAttribute('viewBox', '0 0 16 16');
  icon.setAttribute('fill', 'none');
  icon.classList.add('wishlist-quick-add__icon');

  const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  path.setAttribute('d', 'M3.33333 8H12.6667M8 3.33333V12.6667');
  path.setAttribute('stroke', 'currentColor');
  path.setAttribute('stroke-linecap', 'square');
  path.setAttribute('stroke-linejoin', 'round');
  icon.appendChild(path);
  fragment.appendChild(icon);

  return fragment;
}

function restoreWishlistQuickAddIcon(cartButton) {
  const label = cartButton.getAttribute('aria-label') || 'In den Warenkorb legen';
  cartButton.replaceChildren(...buildWishlistQuickAddIcon(label).childNodes);
}

function watchWishlistQuickAddLoading(cartButton) {
  if (cartButton.dataset.wishlistQuickAddWatcher === 'true') return;
  cartButton.dataset.wishlistQuickAddWatcher = 'true';

  const observer = new MutationObserver(() => {
    if (cartButton.querySelector('.wishlist-quick-add__icon')) {
      cartButton.classList.remove('is-loading');
      return;
    }

    cartButton.classList.add('is-loading');
    restoreWishlistQuickAddIcon(cartButton);
  });

  observer.observe(cartButton, { childList: true, subtree: true, characterData: true });
}

function applyWishlistQuickAddButtons() {
  const cards = document.querySelectorAll('.wishlist-page-main .wishlist-grid1');
  if (!cards.length) return false;

  for (const card of cards) {
    const contentSection = card.querySelector('.product-content-sec');
    const moveCartButton = card.querySelector('.movecart-button');
    if (!contentSection || !moveCartButton) continue;

    if (moveCartButton.parentElement !== contentSection) {
      contentSection.appendChild(moveCartButton);
    }

    moveCartButton.classList.add('wishlist-quick-add');

    const cartButton = moveCartButton.querySelector('.cartButtonStyle');
    if (!cartButton || cartButton.classList.contains('addAllToCartButton')) continue;

    cartButton.classList.add('wishlist-quick-add__button');
    watchWishlistQuickAddLoading(cartButton);

    if (cartButton.dataset.wishlistQuickAddReady === 'true') continue;

    const label = cartButton.textContent.trim() || 'In den Warenkorb legen';
    cartButton.dataset.wishlistQuickAddReady = 'true';
    cartButton.setAttribute('aria-label', label);
    cartButton.replaceChildren(...buildWishlistQuickAddIcon(label).childNodes);
  }

  return true;
}

function setupWishlistPage() {
  applyWishlistPageScheme();
  applyWishlistDefaultGrid();
  applyWishlistQuickAddButtons();
}

function initWishlistPage() {
  if (!window.location.pathname.includes('wg-wishlist')) return;

  setupWishlistPage();

  [500, 1000, 2000, 3500, 5000, 7000, 10000].forEach((delay) => {
    window.setTimeout(setupWishlistPage, delay);
  });
}

function onDomReady(callback) {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', callback);
  } else {
    callback();
  }
}

function shouldSkipContactLinkNode(node) {
  const parent = node.parentElement;
  if (!parent) return true;

  return Boolean(parent.closest('a, script, style, textarea, input, select, button, code, pre'));
}

function linkifyPlainEmails(container) {
  if (!container) return;

  const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      if (!node.nodeValue.includes('@')) return NodeFilter.FILTER_REJECT;
      if (shouldSkipContactLinkNode(node)) return NodeFilter.FILTER_REJECT;
      return NodeFilter.FILTER_ACCEPT;
    }
  });

  const textNodes = [];
  while (walker.nextNode()) {
    textNodes.push(walker.currentNode);
  }

  for (const node of textNodes) {
    const text = node.nodeValue;
    EMAIL_LINK_PATTERN.lastIndex = 0;

    if (!EMAIL_LINK_PATTERN.test(text)) continue;
    EMAIL_LINK_PATTERN.lastIndex = 0;

    const fragment = document.createDocumentFragment();
    let lastIndex = 0;
    let match;

    while ((match = EMAIL_LINK_PATTERN.exec(text)) !== null) {
      if (match.index > lastIndex) {
        fragment.appendChild(document.createTextNode(text.slice(lastIndex, match.index)));
      }

      const link = document.createElement('a');
      link.href = `mailto:${match[0]}`;
      link.textContent = match[0];
      link.className = 'link';
      fragment.appendChild(link);
      lastIndex = match.index + match[0].length;
    }

    if (lastIndex < text.length) {
      fragment.appendChild(document.createTextNode(text.slice(lastIndex)));
    }

    node.parentNode.replaceChild(fragment, node);
  }
}

function linkifyPolicyContactInfo() {
  linkifyPlainEmails(document.querySelector('.shopify-policy__body'));
}

function syncPdpWishlistButtonVariant(form, variantId) {
  if (!form || !variantId) return;

  const wishlistButton = form.querySelector('.wf-wishlist-button');
  if (!wishlistButton) return;

  wishlistButton.setAttribute('variant-id', String(variantId));
}

const KLAVIYO_BIS_SYNC_DELAY_MS = 600;

function isVariantSoldOut(variant, form) {
  if (variant?.inventory_policy === 'continue') return false;
  if (variant?.available === false) return true;
  if (typeof variant?.inventory_quantity === 'number') return variant.inventory_quantity <= 0;

  const submit = form?.querySelector('buy-buttons button[type="submit"]');
  return Boolean(submit?.disabled);
}

function hasKlaviyoBisButton(form) {
  const buyButtons = form?.querySelector('buy-buttons');
  if (!buyButtons) return false;

  return [...buyButtons.querySelectorAll('button, a')].some((element) => {
    if (element.classList.contains('klaviyo-bis-trigger')) return false;

    const text = element.textContent?.trim().toLowerCase() ?? '';
    return text.includes('benachrichtigung') || text.includes('notify me');
  });
}

function updateKlaviyoBisButtonVisibility(form) {
  const buyButtons = form?.querySelector('buy-buttons');
  const submit = buyButtons?.querySelector('button[type="submit"]');
  if (!buyButtons || !submit) return;

  const variant = getVariantFromProductForm(form);
  const soldOut = isVariantSoldOut(variant, form);
  const hideSubmit = soldOut && hasKlaviyoBisButton(form);

  buyButtons.classList.toggle('buy-buttons--klaviyo-bis-active', hideSubmit);
  submit.toggleAttribute('hidden', hideSubmit);
}

const klaviyoBisObservers = new WeakMap();

function observeKlaviyoBisInjection(form) {
  const buyButtons = form?.querySelector('buy-buttons');
  if (!buyButtons) return;

  klaviyoBisObservers.get(form)?.disconnect();

  const observer = new MutationObserver(() => {
    updateKlaviyoBisButtonVisibility(form);
  });

  observer.observe(buyButtons, { childList: true, subtree: true });
  klaviyoBisObservers.set(form, observer);
  updateKlaviyoBisButtonVisibility(form);
}

function getVariantFromProductForm(form) {
  const variantScript = document.querySelector(`variant-picker[form-id="${CSS.escape(form.id)}"]`)?.querySelector('script[data-variant]');

  if (variantScript?.textContent) {
    try {
      return JSON.parse(variantScript.textContent);
    } catch {
      // Fall through.
    }
  }

  const variantId = form.querySelector('input[name="id"]')?.value;
  return variantId ? { id: Number(variantId) } : null;
}

function reinitializeKlaviyoBackInStock() {
  try {
    window._klaviyo?.init?.call(window._klaviyo);
  } catch (error) {
    console.warn('[Klaviyo BIS] _klaviyo.init failed:', error);
  }

  try {
    window.klaviyo?.enable?.('backinstock');
  } catch (error) {
    console.warn('[Klaviyo BIS] klaviyo.enable(backinstock) failed:', error);
  }
}

let klaviyoBisSyncTimer = null;

function syncKlaviyoBackInStock(form) {
  if (!form) return;

  if (klaviyoBisSyncTimer) {
    window.clearTimeout(klaviyoBisSyncTimer);
  }

  klaviyoBisSyncTimer = window.setTimeout(() => {
    klaviyoBisSyncTimer = null;

    const variant = getVariantFromProductForm(form);
    updateKlaviyoBisButtonVisibility(form);

    if (!isVariantSoldOut(variant, form)) return;
    if (hasKlaviyoBisButton(form)) return;

    reinitializeKlaviyoBackInStock();

    // Klaviyo injects async — check once more shortly after.
    window.setTimeout(() => {
      updateKlaviyoBisButtonVisibility(form);

      if (!hasKlaviyoBisButton(form)) {
        reinitializeKlaviyoBackInStock();
      }
    }, 1200);
  }, KLAVIYO_BIS_SYNC_DELAY_MS);
}

function handlePdpVariantChange(event) {
  const form = event.target;
  if (!form?.matches?.('form.shopify-product-form')) return;

  syncPdpWishlistButtonVariant(form, event.detail?.variant?.id);
  syncKlaviyoBackInStock(form);
}

function initKlaviyoBackInStockOnProductPage() {
  if (!window.location.pathname.includes('/products/')) return;

  document.querySelectorAll('form.shopify-product-form').forEach((form) => {
    observeKlaviyoBisInjection(form);
    syncKlaviyoBackInStock(form);
  });
}

document.addEventListener('variant:change', handlePdpVariantChange);
document.addEventListener('product:rerender', (event) => {
  if (!event.target?.matches?.('form.shopify-product-form')) return;
  if (event.detail?.productChange) return;

  observeKlaviyoBisInjection(event.target);
  syncKlaviyoBackInStock(event.target);
});

function setupDesktopProductCardHover(card) {
  if (card.dataset.desktopHoverReady === 'true') {
    return;
  }

  const carousel = card.querySelector('.product-card__media-carousel');
  if (!carousel) {
    return;
  }

  card.dataset.desktopHoverReady = 'true';

  const onEnter = () => {
    if (!window.matchMedia('screen and (min-width: 700px)').matches) {
      return;
    }

    if (typeof carousel.select === 'function' && carousel.cells && carousel.cells.length > 1) {
      carousel.select(1);
    }
  };

  const onLeave = () => {
    if (!window.matchMedia('screen and (min-width: 700px)').matches) {
      return;
    }

    if (typeof carousel.select === 'function') {
      carousel.select(0);
    }
  };

  card.addEventListener('mouseenter', onEnter);
  card.addEventListener('mouseleave', onLeave);
}

function initDesktopProductCardHovers(root = document) {
  root.querySelectorAll('product-card.product-card--diemer').forEach(setupDesktopProductCardHover);
}

function observeDesktopProductCardHovers() {
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (!(node instanceof Element)) {
          continue;
        }

        if (node.matches('product-card.product-card--diemer')) {
          setupDesktopProductCardHover(node);
        }

        node.querySelectorAll('product-card.product-card--diemer').forEach(setupDesktopProductCardHover);
      }
    }
  });

  observer.observe(document.body, { childList: true, subtree: true });
}

function isInsideProductListSlider(carousel) {
  return Boolean(
    carousel.closest(
      '.product-list--carousel, .sm-max\\:product-list--carousel, .md-max\\:product-list--carousel, .search-drawer__product-carousel'
    )
  );
}

function setupMobileProductCardCarousel(carousel) {
  if (carousel.dataset.mobileSwipeReady === 'true') {
    return;
  }

  if (!window.matchMedia('screen and (max-width: 699px)').matches) {
    return;
  }

  // Nested image swipe fights the outer section slider — keep cards swipe-free there
  if (isInsideProductListSlider(carousel)) {
    return;
  }

  carousel.dataset.mobileSwipeReady = 'true';

  let touchStartX = 0;
  let touchStartY = 0;

  carousel.addEventListener(
    'touchstart',
    (event) => {
      touchStartX = event.touches[0].clientX;
      touchStartY = event.touches[0].clientY;
    },
    { passive: true }
  );

  carousel.addEventListener(
    'touchmove',
    (event) => {
      if (event.touches.length !== 1) {
        return;
      }

      const deltaX = event.touches[0].clientX - touchStartX;
      const deltaY = event.touches[0].clientY - touchStartY;

      if (Math.abs(deltaX) <= Math.abs(deltaY)) {
        return;
      }

      const maxScrollLeft = carousel.scrollWidth - carousel.clientWidth;
      const atStart = carousel.scrollLeft <= 0;
      const atEnd = carousel.scrollLeft >= maxScrollLeft - 1;

      if ((atStart && deltaX > 0) || (atEnd && deltaX < 0)) {
        return;
      }

      event.stopPropagation();
    },
    { passive: true }
  );
}

function initMobileProductCardCarousels(root = document) {
  root.querySelectorAll('product-card.product-card--diemer .product-card__media-carousel').forEach(setupMobileProductCardCarousel);
}

function observeMobileProductCardCarousels() {
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (!(node instanceof Element)) {
          continue;
        }

        if (node.matches('.product-card__media-carousel')) {
          setupMobileProductCardCarousel(node);
        }

        if (node.matches('product-card.product-card--diemer')) {
          node.querySelectorAll('.product-card__media-carousel').forEach(setupMobileProductCardCarousel);
        }

        node.querySelectorAll('product-card.product-card--diemer .product-card__media-carousel').forEach(setupMobileProductCardCarousel);
      }
    }
  });

  observer.observe(document.body, { childList: true, subtree: true });
}

onDomReady(() => {
  linkifyPolicyContactInfo();
  initWishlistPage();
  initKlaviyoBackInStockOnProductPage();
  initDesktopProductCardHovers();
  observeDesktopProductCardHovers();
  initMobileProductCardCarousels();
  observeMobileProductCardCarousels();

  if (!window.location.pathname.includes('wg-wishlist')) return;

  const observer = new MutationObserver(() => {
    setupWishlistPage();
  });

  observer.observe(document.body, { childList: true, subtree: true });
});
